import { cloneDeep, isEqual } from 'lodash'
import { createUUID } from '../../utils'
import { getCaseNodeDataTemplate, getIfElseNodeDataTemplate, NodeFactory, PaletteNodes } from './nodes'
import { LinkFactory } from './links'
import { findNearestEmptyPosition } from '../../views/canvas/auto-layout-workflow'
import { NodeType } from '../../core/workflow'
import { current } from 'immer'
import _ from 'lodash'
import toast from 'react-hot-toast'
import { getCompatibleNodes } from '../../utils/available-nodes'
import { g } from '@joint/core'

export class WorkflowController {
  /**
   * Converts the workflow state into a raw format suitable for storage or export.
   * This method extracts nodes and edges from the workflow state and formats them.
   *
   * - Nodes are directly converted to an array of their values.
   * - Edges are mapped to include their properties, excluding the `label` property if it is an empty string.
   *
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {WorkflowDefinitionRaw} An object containing the nodes and edges in raw format.
   */
  static parseDataToRawFormat(workflowState) {
    return {
      nodes: Object.values(workflowState.nodes),
      edges: Object.values(workflowState.links).map(({ label, ...link }) => ({
        ...link,
        ...(label ? { label } : {}) // remove label property when it's empty string
      }))
    }
  }

  /**
   * Adds data from raw input to the workflow state.
   * @param {WorkflowDefinitionRaw} rawData - The raw data containing nodes and edges.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static addDataFromRaw(rawData, workflowState) {
    const { nodes, edges } = rawData

    // Reset workflow state
    workflowState.nodes = {}
    workflowState.links = {}
    workflowState.lastChipIndex = 0 // Reset chip index when loading new data

    let chipCounter = 1
    workflowState.nodes = nodes
      .filter(next => next.node_uid && next.type)
      .reduce((prev, next) => {
        // If chip is missing, assign a new chip number
        if (typeof next.chip === 'undefined') {
          next.chip = chipCounter++
        } else {
          // If chip exists, keep it and update counter if needed
          chipCounter = Math.max(chipCounter, Number(next.chip) + 1)
        }
        return {
          ...prev,
          [next.node_uid]: NodeFactory.fromRawNode(next)
        }
      }, {})

    workflowState.links = edges
      .filter(next => next.edge_uid && next.source && next.target)
      .reduce(
        (prev, next) => ({
          ...prev,
          [next.edge_uid]: LinkFactory.fromRawEdge(next)
        }),
        {}
      )

    // Set lastChipIndex to the max chip in loaded nodes
    const maxChip = Object.values(workflowState.nodes)
      .map(n => Number(n.chip) || 0)
      .reduce((a, b) => Math.max(a, b), 0)
    workflowState.lastChipIndex = maxChip

    // Scan and repair any missing targets for cases, conditions, default targets, and else targets
    Object.values(workflowState.nodes).forEach(node => {
      const position = node.ui?.position || { x: 100, y: 100 }
      if (node.type === NodeType.CASE) {
        if (node.data?.cases) {
          node.data.cases = node.data.cases.map(caseItem => {
            if (caseItem.target && !workflowState.nodes[caseItem.target]) {
              return this.#createCaseItemTemporaryTarget(caseItem, node.node_uid, position, workflowState)
            }
            return caseItem
          })
        }
        if (node.data?.default_target && !workflowState.nodes[node.data.default_target]) {
          const defaultLink = Object.values(workflowState.links).find(
            link => link.source === node.node_uid && (link.metadata?.isDefault || link.label === 'default')
          )
          const defaultTarget = this.#createCaseItemTemporaryTarget(
            {
              condition: 'default',
              edge_uid: defaultLink?.edge_uid || '',
              target: node.data.default_target,
              metadata: { isDefault: true }
            },
            node.node_uid,
            position,
            workflowState
          )
          node.data.default_target = defaultTarget.target
        }
      } else if (node.type === NodeType.IF_ELSE) {
        if (node.data?.conditions) {
          node.data.conditions = node.data.conditions.map(cond => {
            if (cond.target && !workflowState.nodes[cond.target]) {
              return this.#createConditionItemTemporaryTarget(cond, node.node_uid, position, workflowState)
            }
            return cond
          })
        }
        if (node.data?.else_target && !workflowState.nodes[node.data.else_target]) {
          const elseLink = Object.values(workflowState.links).find(
            link => link.source === node.node_uid && (link.metadata?.isElse || link.label === 'else')
          )
          const elseTarget = this.#createConditionItemTemporaryTarget(
            {
              condition: 'else',
              edge_uid: elseLink?.edge_uid || '',
              target: node.data.else_target,
              metadata: { isElse: true }
            },
            node.node_uid,
            position,
            workflowState
          )
          node.data.else_target = elseTarget.target
        }
      }
    })

    // Clean up any links that point to non-existent nodes
    Object.keys(workflowState.links).forEach(edge_uid => {
      const link = workflowState.links[edge_uid]
      if (!workflowState.nodes[link.source] || !workflowState.nodes[link.target]) {
        delete workflowState.links[edge_uid]
      }
    })
  }

  /**
   * Evaluates if a node can accept more connections based on its connection limits.
   * Connection limit values:
   *   -1: unlimited connections
   *    0: no connections allowed
   *   >0: specific number of connections allowed
   *
   * @param {Node} node - The source node object (checks outgoing limit)
   * @param {Node|null} targetNode - The target node object (checks incoming limit)
   * @param {WorkflowState} workflowState - The workflow state object
   * @param {string|null} replacingLinkId - Optional. The ID of the link being replaced (excluded from count)
   * @returns {{incoming: boolean, outgoing: boolean}} Object indicating if connections are allowed
   *          - outgoing: whether source node can create more outgoing connections
   *          - incoming: whether target node can accept more incoming connections
   */
  static evaluateNodeConnectionLimits(node, targetNode = null, workflowState, replacingLinkId = null) {
    const { links } = workflowState
    const nodeId = node.node_uid
    const targetId = targetNode?.node_uid

    // === EVALUATE SOURCE NODE OUTGOING LIMIT ===
    const outportAllowed = node.ui?.metadata?.outport ?? true
    let outgoingLimit = node.ui?.connection_limits?.outgoing
    if (outgoingLimit !== undefined && outgoingLimit !== null) {
      outgoingLimit = Number(outgoingLimit)
    }

    // Fast path: if outport not allowed, deny outgoing
    if (!outportAllowed) {
      return { incoming: targetNode ? (targetNode.ui?.metadata?.inport ?? true) : true, outgoing: false }
    }

    // Determine if source node has an outgoing limit to check
    const hasOutgoingLimit = outgoingLimit !== undefined && outgoingLimit !== null && outgoingLimit !== -1

    let outgoingAllowed = true
    // Track if the link being replaced already points to this exact target (no net change scenario)
    let isReplacingSameTarget = false

    // Get incoming limit early so we can use it in the check
    let incomingLimit = targetNode?.ui?.connection_limits?.incoming
    if (incomingLimit !== undefined && incomingLimit !== null) {
      incomingLimit = Number(incomingLimit)
    }
    const hasIncomingLimit = incomingLimit !== undefined && incomingLimit !== null && incomingLimit !== -1

    // Only iterate through links if we need to check limits
    if (hasOutgoingLimit || (targetNode && hasIncomingLimit)) {
      let outboundCount = 0
      let targetInboundCount = 0

      // Single pass through links to count both outbound and inbound
      for (const link of Object.values(links)) {
        // Check if the link being replaced already points to the new target
        if (replacingLinkId && link.edge_uid === replacingLinkId) {
          if (targetId && link.target === targetId) {
            // We're replacing a link that already points to this target - true replacement (no net change)
            isReplacingSameTarget = true
          }
          continue // Skip counting the link being replaced for outgoing limit
        }

        if (link.source === nodeId) {
          outboundCount++
        }

        // Count inbound to target - count ALL links pointing to target (excluding the one being replaced)
        // This is important: we count all incoming links regardless of source
        if (targetId && link.target === targetId) {
          targetInboundCount++
        }
      }

      // Evaluate outgoing
      if (hasOutgoingLimit) {
        if (outgoingLimit === 0) {
          outgoingAllowed = isReplacingSameTarget
        } else {
          // For outgoing, we allow if replacing same target OR under limit
          outgoingAllowed = isReplacingSameTarget || outboundCount < outgoingLimit
        }
      }

      // If no target node, return with outgoing evaluation only
      if (!targetNode) {
        return { incoming: true, outgoing: outgoingAllowed }
      }

      // Evaluate incoming
      const targetInportAllowed = targetNode.ui?.metadata?.inport ?? true
      if (!targetInportAllowed) {
        return { incoming: false, outgoing: outgoingAllowed }
      }

      let incomingAllowed = true
      if (hasIncomingLimit) {
        if (incomingLimit === 0) {
          // Only allow if we're replacing a link that already points to this target
          incomingAllowed = isReplacingSameTarget
        } else {
          // For incoming limit check:
          // - If we're replacing a link that already points to this target, allow (no net change)
          // - Otherwise, check if adding one more would exceed the limit
          if (isReplacingSameTarget) {
            incomingAllowed = true
          } else {
            // targetInboundCount is the count of links already pointing to target (excluding the link being replaced)
            // If we connect to this target, it will have targetInboundCount + 1 incoming connections
            incomingAllowed = targetInboundCount < incomingLimit
          }
        }
      }

      return { incoming: incomingAllowed, outgoing: outgoingAllowed }
    }

    // No limits to check - allow all
    if (!targetNode) {
      return { incoming: true, outgoing: true }
    }

    const targetInportAllowed = targetNode.ui?.metadata?.inport ?? true
    return { incoming: targetInportAllowed, outgoing: true }
  }

  /**
   * Evaluates if a connection from a node to a target node type is allowed.
   * @param {string} nodeId - The source node ID
   * @param {string} targetNodeType - The target node type
   * @param {WorkflowState} workflowState - The workflow state object
   * @returns {boolean} Whether the connection is allowed
   */
  static evaluateNodeAvailableConnections(nodeId, targetNodeType, workflowState) {
    const nodeData = workflowState.nodes[nodeId]

    // Fast path: if node doesn't exist, deny connection
    if (!nodeData) {
      return false
    }

    const connectionList = nodeData.available_connections

    // Fast path: if no restrictions defined, allow all connections
    if (!connectionList || connectionList.length === 0) {
      return true
    }

    const allowedConnections = new Set(connectionList)

    // Check palette groups and items
    for (const paletteGroup of PaletteNodes) {
      // Check if entire group is allowed
      if (allowedConnections.has(paletteGroup.id)) {
        // If group is allowed, check if target type exists in this group
        if (paletteGroup.items?.some(item => item.type === targetNodeType)) {
          return true
        }
        continue
      }

      // Check individual items in group - only if items exist
      const items = paletteGroup.items
      if (!items) {
        continue
      }

      for (const item of items) {
        // Skip items that don't match target type
        if (item.type !== targetNodeType) {
          continue
        }

        // Check if item_type or subtype is allowed
        if (
          (item.item_type && allowedConnections.has(item.item_type)) ||
          (item.subtype && allowedConnections.has(item.subtype))
        ) {
          return true
        }
      }
    }

    return false
  }

  static createNodeDefaultFormData = (nodeData, schema = null, nodeUid = null) => {
    const defaultFormData = nodeData?.data?.form || {}

    // Recursively extract default values from a JSON Schema properties map.
    // Works at any nesting depth:
    //   - Scalar with `default`          → use the value directly
    //   - Object with `properties`       → always recurse so sub-levels are initialised
    //   - Object with `default` as well  → merge the schema default with the deeper defaults
    //     (deeper defaults win so they can override a partial object default)
    const extractDefaults = properties => {
      const result = {}
      for (const [key, schema] of Object.entries(properties || {})) {
        if (schema.type === 'object' && schema.properties) {
          const nested = extractDefaults(schema.properties)
          // Merge: schema-level default (if any) is the base; recursive defaults overlay it
          result[key] = schema.default !== undefined ? { ...schema.default, ...nested } : nested
        } else if (schema.default !== undefined) {
          result[key] = schema.default
        }
      }
      return result
    }

    // Deep-merge helper: overlays `source` onto `target` recursively.
    // Only fills in missing keys — never overwrites existing form data.
    const deepMergeDefaults = (target, source) => {
      for (const [key, value] of Object.entries(source)) {
        if (target[key] === undefined) {
          target[key] = value
        } else if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value) &&
          typeof target[key] === 'object' &&
          !Array.isArray(target[key])
        ) {
          deepMergeDefaults(target[key], value)
        }
      }
    }

    const targetSchema = schema || nodeData?.json_schema
    if (targetSchema?.properties?.data?.properties) {
      const schemaDefaults = extractDefaults(targetSchema.properties.data.properties)
      deepMergeDefaults(defaultFormData, schemaDefaults)
    }

    return {
      node_uid: nodeUid || nodeData.id,
      label: nodeData.name || '',
      type: nodeData?.type || '',
      description: nodeData?.description || '',
      data: defaultFormData
    }
  }

  /**
   * Adds a new node to the workflow state.
   * @param {string} type - The type of the node.
   * @param {DiagramPosition} position - The position of the node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {string} The unique ID of the created node.
   * @param {string|null} nodeId - Optional. The ID of the node to use. If null, a new UUID will be generated.
   * @param {Object|null} nodeData - Optional. Additional data for the node, such as name, description, schemas, etc.
   * @param {Array} paletteNodes - The palette nodes array from state.ui.palette.nodes
   */
  static addNewNode(type, position, workflowState, nodeId = null, nodeData = null, paletteNodes = []) {
    if (!type || !position || !workflowState) {
      throw new Error('Missing required parameters: type, position, and workflowState are required')
    }

    // Disallow adding multiple start nodes
    if (type === NodeType.START || type === 'start') {
      const alreadyHasStart = Object.values(workflowState.nodes || {}).some(
        n => n.type === NodeType.START || n.type === 'start'
      )
      if (alreadyHasStart) {
        return null
      }
    }

    // Generate node_uid once and validate chip numbering with safer fallback
    const node_uid = nodeId || createUUID()
    const chipIndex =
      type === NodeType.TEMPORARY ? (workflowState.lastChipIndex ?? 0) : (workflowState.lastChipIndex ?? 0) + 1
    workflowState.lastChipIndex = chipIndex

    // Get node data template
    let data = {}
    if (type === NodeType.CASE) {
      data = getCaseNodeDataTemplate()
    } else if (type === NodeType.IF_ELSE) {
      data = getIfElseNodeDataTemplate()
    }

    // Optimized schema extraction with better boolean logic
    let nodeSchemaData = { json_schema: {}, ui_schema: {} }

    // Check if nodeData has valid schemas with cleaner logic
    const hasValidSchemas =
      nodeData?.json_schema &&
      Object.keys(nodeData.json_schema).length > 0 &&
      nodeData?.ui_schema &&
      Object.keys(nodeData.ui_schema).length > 0

    if (hasValidSchemas) {
      // Use existing schemas from nodeData
      nodeSchemaData = {
        json_schema: nodeData.json_schema,
        ui_schema: nodeData.ui_schema
      }
    } else if (paletteNodes.length > 0) {
      // Find matching palette node only if schemas not provided
      const targetSubtype = nodeData?.subtype || ''
      const matchingNode = paletteNodes.find(node => node.type === type && node.subtype === targetSubtype)

      if (matchingNode) {
        nodeSchemaData = {
          json_schema: matchingNode.json_schema || {},
          ui_schema: matchingNode.ui_schema || {}
        }
      }
    }

    // Extract commonly used values once to avoid repeated property access
    const nodeIcon = nodeData?.node_icon_link || ''
    const subtype = nodeData?.subtype || ''
    const label = nodeData?.name || type
    const description = nodeData?.description || ''
    const tooltipInfo = nodeData?.node_tooltip_info || ''

    // Use nullish coalescing for boolean values to handle explicit false correctly
    const multipleOutConnection = nodeData?.node_have_multiple_out_connection ?? true
    const inportAllowed = nodeData?.node_in_connection_allowed ?? true
    const outportAllowed = nodeData?.node_out_connection_allowed ?? true
    const connectionLimits = nodeData?.connection_limits || {}

    const defaultFormData = this.createNodeDefaultFormData(nodeData || {}, nodeSchemaData.json_schema, node_uid)

    const newNodeData = {
      node_uid,
      label,
      icon: nodeIcon,
      type,
      subtype,
      position: findNearestEmptyPosition(position, workflowState.nodes),
      metadata: nodeData?.metadata || {},
      data: {
        ...data,
        schema: nodeSchemaData.json_schema,
        description: description,
        form: data?.form || defaultFormData // initialize empty form so the properties panel can sync immediately
      },
      ui: {
        schema: nodeSchemaData.ui_schema,
        metadata: {
          description,
          multiple_out_connection: multipleOutConnection,
          inport: inportAllowed,
          outport: outportAllowed,
          info: tooltipInfo
        },
        available_connections: nodeData?.available_connections || [],
        connection_limits: connectionLimits,
        validation: nodeData?.validation || {
          isValid: false,
          errors: []
        },
        icon: nodeIcon
      },
      chip: chipIndex
    }

    // Create node configuration object with optimized structure
    const node = NodeFactory.createNode(newNodeData)

    // Add and post-process node
    this.#addNode(node, workflowState)
    this.#postProcessNode(node, workflowState)

    return node_uid
  }

  /**
   * Clones an existing node and adds it to the workflow state.
   * @param {string} id - The ID of the node to clone.
   * @param {DiagramPosition} position - The position of the new node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {string} The unique ID of the cloned node.
   */
  static cloneNode(id, position, workflowState) {
    const chipIndex = (workflowState.lastChipIndex || 0) + 1
    workflowState.lastChipIndex = chipIndex

    const copiedNode = cloneDeep(workflowState.nodes[id])
    const node = { ...copiedNode, node_uid: createUUID(), chip: chipIndex }
    node.ui.position = findNearestEmptyPosition(position, workflowState.nodes) || copiedNode.ui.position
    this.#addNode(node, workflowState)
    this.#postProcessNode(node, workflowState)
    return node.node_uid
  }

  /**
   * Removes selected nodes and links from the workflow state.
   * @param {SelectionState} selection - The selection containing nodes and links to remove.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @param {boolean} [removeCaseCondition]
   */
  static removeSelection(selection, workflowState, removeCaseCondition) {
    selection.nodes.forEach(nodeId => this.removeNode(nodeId, workflowState, removeCaseCondition))
    selection.links.forEach(linkId => this.removeLink(linkId, workflowState, removeCaseCondition))
  }

  /**
   * Pastes nodes and links from the clipboard to the workflow state.
   * @param {ClipboardState} clipboard - The clipboard containing nodes and links.
   * @param {DiagramPosition} position - The position to paste the nodes and links.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {{nodes: WorkflowNode[], links: WorkflowEdge[]}} The pasted nodes and links.
   */
  static pasteFromClipboard(clipboard, position, workflowState) {
    const mapNodes = {} // {[copiedNodeID]: newNodeID};

    // keep new node id for original node ids
    const nodes = clipboard.nodes.map(n => {
      const chipIndex = (workflowState.lastChipIndex || 0) + 1
      workflowState.lastChipIndex = chipIndex

      const newNode = { ...n, node_uid: createUUID(), chip: chipIndex }
      mapNodes[n.node_uid] = newNode.node_uid
      return newNode
    })

    const links = clipboard.links.map(l => ({
      ...l,
      edge_uid: createUUID(),
      source: mapNodes[l.source], // get new node id for source
      target: mapNodes[l.target] // get new node id for target
    }))

    // adjust position
    const centerPoint = new g.Polyline(nodes.map(node => node.ui.position)).bbox().topLeft()
    const positionOffset = new g.Point(centerPoint).difference(position)

    nodes.forEach(copy => {
      const copyPosition = copy.ui.position
      copyPosition.x -= positionOffset.x
      copyPosition.y -= positionOffset.y
    })

    nodes.forEach(node => this.#addNode(node, workflowState))
    links.forEach(link => this.#addLink(link, workflowState))
    return { nodes, links }
  }

  /**
   * Creates a clipboard object from the selected nodes and links.
   * @param {SelectionState} selection - The selection containing nodes and links.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {ClipboardState} The clipboard object containing nodes and links.
   */
  static createClipboardFromSelection(selection, workflowState) {
    const nodes = selection.nodes.map(nodeId => cloneDeep(workflowState.nodes[nodeId]))
    const links = selection.links.map(linkId => cloneDeep(workflowState.links[linkId]))
    return { nodes, links }
  }

  /**
   * Updates the label of a node.
   * @param {string} id - The ID of the node.
   * @param {string} label - The new label of the node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static updateNodeLabel(id, label, workflowState) {
    const node = workflowState.nodes[id]
    if (!node) {
      return
    }

    // Ensure label is properly set, converting undefined/null to empty string
    const sanitizedLabel = label || ''
    node.data.label = sanitizedLabel
    node.ui.label = sanitizedLabel
  }

  /**
   * Updates the description of a node.
   * @param {string} id - The ID of the node.
   * @param {string} description - The new description of the node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static updateNodeDescription(id, description, workflowState) {
    const node = workflowState.nodes[id]
    if (!node) {
      return
    }

    // Ensure description is properly set, converting undefined/null to empty string
    const sanitizedDescription = description || ''
    node.data.description = sanitizedDescription
    node.ui.description = sanitizedDescription
  }

  /**
   * Updates the properties of a node.
   * @param {string} id - The ID of the node.
   * @param {WorkflowNodeData} data - The new data for the node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static updateNodeProperties(id, data, workflowState) {
    const node = workflowState.nodes[id]

    if (!node) {
      return
    } // node not exist in data

    const previousData = cloneDeep(node.data)
    let updatedData = data

    // add logic to support updating/adding/removing links when adjust case node cases list
    if (node.type === NodeType.CASE) {
      updatedData = this.#updateCaseNodeEdges(id, previousData, data, workflowState)
    } else if (node.type === NodeType.IF_ELSE) {
      updatedData = this.#updateIfElseNodeEdges(id, previousData, data, workflowState)
    }
    // Always update the node data with the potentially modified data
    workflowState.nodes[id].data = updatedData
  }
  /**
   * Updates the Validation properties of a node.
   * @param {string} id - The ID of the node.
   * @param {WorkflowNodeData} data - The new data for the node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static updateNodeValidationProperties(id, data, workflowState) {
    const node = workflowState.nodes[id]
    if (!node) {
      return
    }

    // Initialize ui object if it doesn't exist
    if (!node.ui) {
      node.ui = {}
    }

    node.ui.validation = {
      isValid: data.isValid ?? data.is_valid ?? false,
      errors: data.errors || []
    }
  }

  /**
   * Batch updates validation properties for multiple nodes.
   * @param {Object} validationResults - Object with node IDs as keys and validation results as values.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static batchUpdateNodeValidationProperties(validationResults, workflowState) {
    if (!validationResults || typeof validationResults !== 'object') {
      return
    }

    for (const nodeId of Object.keys(validationResults)) {
      const node = workflowState.nodes[nodeId]
      if (!node) {
        continue
      }

      const validationData = validationResults[nodeId]

      // Initialize ui object if it doesn't exist
      if (!node.ui) {
        node.ui = {}
      }

      // Update validation state
      node.ui.validation = {
        isValid: validationData.isValid ?? false,
        errors: validationData.errors || [],
        skipped: validationData.skipped ?? false
      }
    }
  }

  /**
   * Updates the positions of multiple nodes.
   * @param {{id: string, position: DiagramPosition}[]} positionUpdates - An array of position updates.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static updateNodesPosition(positionUpdates, workflowState) {
    positionUpdates.forEach(({ id, position }) => {
      workflowState.nodes[id].ui.position = position
      workflowState.nodes[id].position = position
    })
  }

  /**
   * Removes all links connected to a node.
   * @param {string} nodeId - The ID of the node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static removeNodeLinks(nodeId, workflowState) {
    const connectedLinks = this.#getNodeConnectedLinks(nodeId, workflowState.links)
    connectedLinks.forEach(link => delete workflowState.links[link.edge_uid])
  }

  /**
   * Removes a link from the workflow state and optionally updates associated case/if-else nodes.
   * @param {string} linkId - The ID of the link to remove.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @param {boolean} [removeCaseCondition=false] - Whether to remove the associated case/condition or replace with temp node.
   */
  static removeLink(linkId, workflowState, removeCaseCondition = false) {
    const link = workflowState.links[linkId]
    if (!link) {
      return
    }

    const sourceNode = workflowState.nodes[link.source]

    // Update case/if-else nodes if the source is one of those types
    if (sourceNode?.type === NodeType.CASE) {
      this.#updateCaseNodeWhenRemoveLink(linkId, sourceNode, removeCaseCondition, workflowState)
    } else if (sourceNode?.type === NodeType.IF_ELSE) {
      this.#updateIfElseNodeWhenRemoveLink(linkId, sourceNode, removeCaseCondition, workflowState)
    }

    // Delete the link
    delete workflowState.links[linkId]
  }

  /**
   * Removes a node and connected links from the workflow state.
   * @param {string} id - The ID of the node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @param {boolean} [removeCaseCondition]
   */
  static removeNode(id, workflowState, removeCaseCondition) {
    const node = workflowState.nodes[id]
    const tempNodeIds = new Set()

    if (node) {
      if (node.type === NodeType.CASE) {
        if (node.data?.default_target) {
          const targetNode = workflowState.nodes[node.data.default_target]
          if (targetNode?.type === NodeType.TEMPORARY) {
            tempNodeIds.add(node.data.default_target)
          }
        }
        if (node.data?.cases) {
          node.data.cases.forEach(caseItem => {
            if (caseItem.target) {
              const targetNode = workflowState.nodes[caseItem.target]
              if (targetNode?.type === NodeType.TEMPORARY) {
                tempNodeIds.add(caseItem.target)
              }
            }
          })
        }
      } else if (node.type === NodeType.IF_ELSE) {
        if (node.data?.else_target) {
          const targetNode = workflowState.nodes[node.data.else_target]
          if (targetNode?.type === NodeType.TEMPORARY) {
            tempNodeIds.add(node.data.else_target)
          }
        }
        if (node.data?.conditions) {
          node.data.conditions.forEach(condItem => {
            if (condItem.target) {
              const targetNode = workflowState.nodes[condItem.target]
              if (targetNode?.type === NodeType.TEMPORARY) {
                tempNodeIds.add(condItem.target)
              }
            }
          })
        }
      }
    }

    this.#updateCaseNodesWhenRemoveNode(id, workflowState, removeCaseCondition)
    this.#updateIfElseNodesWhenRemoveNode(id, workflowState, removeCaseCondition)
    this.removeNodeLinks(id, workflowState)
    delete workflowState.nodes[id]

    // Remove the connected temporary nodes and their links
    tempNodeIds.forEach(tempId => {
      this.removeNodeLinks(tempId, workflowState)
      delete workflowState.nodes[tempId]
    })
  }

  static #getNodeTemplate(type) {
    if (type === NodeType.CASE) {
      return getCaseNodeDataTemplate()
    }
    if (type === NodeType.IF_ELSE) {
      return getIfElseNodeDataTemplate()
    }
    return {}
  }

  /**
   * Creates a new node and connects it to an existing node.
   * @param {string} nodeId - The ID of the existing node.
   * @param {DiagramPosition} position - The position of the new node.
   * @param {string} newNodeType - The type of the new node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @param {Array} matchingNode - The matching node
   */
  static createNodeNextConnection(nodeId, position, newNodeType, workflowState, matchingNode = {}) {
    const sourceNode = workflowState.nodes[nodeId]

    // get newNodeType matching node from palette nodes context
    const restrictionStatus = this.evaluateNodeConnectionLimits(sourceNode, null, workflowState)

    // Check if outgoing connections are allowed
    if (!restrictionStatus.outgoing) {
      toast.error(`Node "${sourceNode?.data?.label || ''}" has reached it's outgoing connection limit.`)
      return false
    }

    // Find the palette node matching the new node type and extract its schemas if available
    const dataSchema = matchingNode?.json_schema || {}
    const uiSchema = matchingNode?.ui_schema || {}

    const chipIndex = (workflowState.lastChipIndex || 0) + 1
    workflowState.lastChipIndex = chipIndex

    const sourceNodeType = sourceNode.type

    const sourcePosition = cloneDeep(workflowState.nodes[nodeId]).ui.position
    const newNodeData = this.#getNodeTemplate(newNodeType)
    const newNodeUid = createUUID()
    const defaultFormData = this.createNodeDefaultFormData(matchingNode || {}, dataSchema, newNodeUid)

    const newNode = NodeFactory.createNode({
      type: newNodeType,
      subtype: matchingNode?.subtype || '',
      item_type: matchingNode?.item_type || '',
      node_uid: newNodeUid,
      label: matchingNode?.label || matchingNode?.name || newNodeType,
      icon: matchingNode?.node_icon_link || '',
      position: findNearestEmptyPosition(sourcePosition, workflowState.nodes),
      metadata: matchingNode.metadata || {},
      available_connections: matchingNode?.available_connections || [],
      connection_limits: matchingNode?.connection_limits || {},
      data: {
        ...newNodeData,
        schema: dataSchema,
        description: matchingNode?.description || '',
        form: newNodeData?.form || defaultFormData
      },
      ui: {
        schema: uiSchema,
        icon: matchingNode?.node_icon_link || '',
        available_connections: matchingNode?.available_connections || [],
        connection_limits: matchingNode?.connection_limits || {}
      },
      chip: chipIndex
    })

    const newLink = LinkFactory.createEdge({
      source: nodeId,
      target: newNode.node_uid,
      label: sourceNodeType === NodeType.CASE || sourceNodeType === NodeType.IF_ELSE ? '??' : ''
    })

    this.#addNode(newNode, workflowState)
    this.#addLink(newLink, workflowState)
    this.#postProcessNode(newNode, workflowState)

    if (sourceNodeType === NodeType.CASE) {
      const data = cloneDeep(sourceNode.data)
      data.cases.push({
        edge_uid: newLink.edge_uid,
        condition: '??',
        target: newNode.node_uid
      })

      data.form = {
        ...data.form,
        cases: data.cases,
        default_target: data.default_target,
        data: {
          ...data.form?.data,
          cases: data.cases,
          default_target: data.default_target
        }
      }

      this.updateNodeProperties(nodeId, data, workflowState)
    } else if (sourceNodeType === NodeType.IF_ELSE) {
      const data = cloneDeep(sourceNode.data)
      data.conditions.push({
        edge_uid: newLink.edge_uid,
        condition: '??',
        target: newNode.node_uid
      })

      data.form = {
        ...data.form,
        conditions: data.conditions,
        else_target: data.else_target,
        data: {
          ...data.form?.data,
          conditions: data.conditions,
          else_target: data.else_target
        }
      }
      this.updateNodeProperties(nodeId, data, workflowState)
    }

    return true
  }

  /**
   * Searches for nodes in the palette that match the search term and are compatible with the source node's available connections.
   * @param {*} searchTerm : The term to search for.
   * @param {*} sourceNodeId : The ID of the source node.
   * @param {*} paletteNodes : The list of nodes in the palette.
   * @param {*} workflowState : The current state of the workflow.
   * @returns {PaletteNodes} The filtered list of palette nodes that match the search term and are compatible with the source node.
   */
  static searchHaloNodes(searchTerm, sourceNodeId, paletteNodes, workflowState) {
    const sourceNode = workflowState.nodes[sourceNodeId]

    if (!sourceNode) {
      return []
    }

    if (searchTerm.trim() === '') {
      return getCompatibleNodes(sourceNode, current(paletteNodes))
    }

    const nodeList = getCompatibleNodes(sourceNode, current(paletteNodes))

    // search all properties of palette nodes
    const result = _.filter(nodeList, obj =>
      _.some(obj, value => _.isString(value) && value.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    return result || []
  }

  /**
   * Replaces an existing node with a new node of the specified type and moves all links from the old node to the new node.
   * @param {string} nodeId - The ID of the node to be replaced.
   * @param {string} newNodeType - The type of the new node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {string} The unique ID of the newly created node.
   */
  static replaceNode(nodeId, newNodeType, workflowState, paletteNodes = []) {
    const oldNode = workflowState.nodes[nodeId]
    if (!oldNode) {
      throw new Error(`Node with ID ${nodeId} does not exist.`)
    }

    const matchingNode = paletteNodes.find(node => node.type === newNodeType)
    const { json_schema = {}, ui_schema = {}, name, node_icon_link = '' } = matchingNode || {}

    const newNodeData =
      newNodeType === NodeType.CASE
        ? getCaseNodeDataTemplate()
        : newNodeType === NodeType.IF_ELSE
          ? getIfElseNodeDataTemplate()
          : {}

    const chipIndex = (workflowState.lastChipIndex || 0) + 1
    workflowState.lastChipIndex = chipIndex

    const newNodeUid = createUUID()
    const defaultFormData = this.createNodeDefaultFormData(matchingNode || {}, json_schema, newNodeUid)

    const newNode = NodeFactory.createNode({
      type: newNodeType,
      subtype: matchingNode?.subtype || '',
      item_type: matchingNode?.item_type || '',
      node_uid: newNodeUid,
      label: name || matchingNode?.name || newNodeType,
      icon: node_icon_link,
      position: oldNode.ui.position,
      metadata: matchingNode.metadata || {},
      available_connections: matchingNode?.available_connections || [],
      connection_limits: matchingNode?.connection_limits || {},
      data: {
        ...newNodeData,
        schema: json_schema,
        description: matchingNode?.description || '',
        form: newNodeData?.form || defaultFormData
      },
      ui: {
        schema: ui_schema,
        icon: node_icon_link,
        available_connections: matchingNode?.available_connections || [],
        connection_limits: matchingNode?.connection_limits || {}
      },
      chip: chipIndex
    })

    const connectedLinks = this.#getNodeConnectedLinks(nodeId, workflowState.links)
    this.#addNode(newNode, workflowState)

    connectedLinks.forEach(({ edge_uid, source, target, label, metadata }) => {
      let newLinkId

      if (source === nodeId) {
        newLinkId = this.addNewLink({ source: newNode.node_uid, target, label, metadata }, workflowState)
      } else if (target === nodeId) {
        newLinkId = this.addNewLink({ source, target: newNode.node_uid, label, metadata }, workflowState)
      }

      // If the connected node is a CASE node, update its cases array
      const connectedNode = workflowState.nodes[source === nodeId ? target : source]
      if (connectedNode?.type === NodeType.CASE) {
        // Update the relevant case item in data.cases
        connectedNode.data.cases = connectedNode.data.cases.map(caseItem =>
          caseItem.edge_uid === edge_uid || caseItem.target === nodeId
            ? {
                ...caseItem,
                target: newNode.node_uid,
                edge_uid: newLinkId
              }
            : caseItem
        )
        // Also update default_target if needed
        if (connectedNode.data.default_target === nodeId) {
          connectedNode.data.default_target = newNode.node_uid
        }
      } else if (connectedNode?.type === NodeType.IF_ELSE) {
        // Check if this is the else_target link
        const isElseTargetLink =
          metadata?.isElse === true || label === 'else' || connectedNode.data.else_target === nodeId

        if (isElseTargetLink || connectedNode.data.else_target === nodeId) {
          // Update else_target to point to the new node
          connectedNode.data.else_target = newNode.node_uid
        }

        // Update the relevant condition in data.conditions - check both by edge_uid AND by target
        connectedNode.data.conditions = connectedNode.data.conditions.map(condition =>
          condition.edge_uid === edge_uid || condition.target === nodeId
            ? {
                ...condition,
                target: newNode.node_uid,
                edge_uid: newLinkId
              }
            : condition
        )
      }
    })

    this.removeNodeLinks(nodeId, workflowState)
    delete workflowState.nodes[nodeId]

    this.#postProcessNode(newNode, workflowState)

    return newNode.node_uid
  }

  /**
   * Adds a new link to the workflow state.
   * @param {WorkflowEdge} data - The data for the new link.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {string} The unique ID of the created link.
   */
  static addNewLink(data, workflowState) {
    const link = LinkFactory.createEdge(data)
    return this.#addLink(link, workflowState)
  }

  /**
   * Gets the links connected to a node in the workflow.
   * @param {string} nodeId - The ID of the node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {WorkflowEdge[]} The links connected to the node.
   */
  static getNodeLinks(nodeId, workflowState) {
    const existingLinks = this.#getNodeConnectedLinks(nodeId, workflowState.links)
    return existingLinks
  }

  /**
   * Updates the label of a link.
   * @param {string} id - The ID of the link.
   * @param {string} label - The new label of the link.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   */
  static updateLinkLabel(id, label, workflowState) {
    const link = workflowState.links[id]
    if (!link) {
      return
    }
    link.label = label
  }

  /**
   * Replaces the target of a link with a new target and updates the source node's cases if necessary.
   * @param {string} id - The ID of the link.
   *  @param {string} sourceId - The ID of the source node.
   * @param {string} targetId - The ID of the new target node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @param {*} linkView - The link view object.
   */
  static replaceLinkTarget(id, sourceId, targetId, workflowState, linkView) {
    const { nodes, links } = workflowState
    let link = links[id]
    const sourceNode = nodes[sourceId]
    const targetNode = nodes[targetId]

    // Fast validation
    if (!targetNode) {
      return false
    }

    if (!sourceNode) {
      return false
    }

    const sourceNodeType = sourceNode.type
    const sourceData = sourceNode.data

    // If link doesn't exist yet (new link being created via drag), we need to handle this differently
    // For CASE/IF_ELSE nodes, check if there's a case/condition that references a link that no longer exists
    let existingEdgeUid = null
    let previousTarget = link?.target

    if (!link && sourceNodeType === NodeType.CASE) {
      // Find case item that might have a stale edge_uid (link was deleted but case still references it)
      for (const caseItem of sourceData.cases) {
        if (caseItem.edge_uid && !links[caseItem.edge_uid]) {
          // This case has a stale reference - this is likely the one being reconnected
          existingEdgeUid = caseItem.edge_uid
          previousTarget = caseItem.target
          break
        }
      }
    } else if (!link && sourceNodeType === NodeType.IF_ELSE) {
      // Check conditions for stale edge_uid
      for (const condItem of sourceData.conditions) {
        if (condItem.edge_uid && !links[condItem.edge_uid]) {
          existingEdgeUid = condItem.edge_uid
          previousTarget = condItem.target
          break
        }
      }
    }

    // Identify if this is a default or else link by checking metadata or matching target
    const isDefaultLink =
      link?.metadata?.isDefault === true ||
      (sourceNodeType === NodeType.CASE && link?.target === sourceData?.default_target)
    const isElseLink =
      link?.metadata?.isElse === true ||
      link?.label === 'else' ||
      (sourceNodeType === NodeType.IF_ELSE && link?.target === sourceData?.else_target)

    // For default/else links without a target in link data, use the source node's data
    if (!previousTarget) {
      if (isDefaultLink && sourceNodeType === NodeType.CASE) {
        previousTarget = sourceData?.default_target
      } else if (isElseLink && sourceNodeType === NodeType.IF_ELSE) {
        previousTarget = sourceData?.else_target
      }
    }

    // Pass the link ID being replaced so it's excluded from the outgoing count
    const evaluationResult = this.evaluateNodeConnectionLimits(
      sourceNode,
      targetNode,
      workflowState,
      link ? id : existingEdgeUid
    )

    // Helper to handle link restoration/removal - avoid code duplication
    const handleLinkFailure = errorMsg => {
      toast.error(errorMsg)
      if (previousTarget && nodes[previousTarget]) {
        this.#restoreLinkToPreviousTarget(linkView, previousTarget)
      } else {
        linkView?.model?.remove()
      }
      return false
    }

    if (!evaluationResult.incoming) {
      return handleLinkFailure(`Node "${targetNode?.data?.label || ''}" has reached it's incoming connection limit.`)
    }

    if (!evaluationResult.outgoing) {
      return handleLinkFailure(`Node "${sourceNode?.data?.label || ''}" has reached it's outgoing connection limit.`)
    }

    if (!this.evaluateNodeAvailableConnections(sourceId, targetNode.type, workflowState)) {
      return handleLinkFailure(
        `Connection from "${sourceNode?.data?.label || ''}" to "${targetNode?.data?.label || ''}" is not allowed.`
      )
    }

    // Helper to cleanup temporary node - check if still referenced AFTER link update
    const cleanupTempNode = nodeId => {
      if (!nodeId || nodeId === targetId) {
        return
      }
      const tempNode = nodes[nodeId]
      if (!tempNode || tempNode.type !== NodeType.TEMPORARY) {
        return
      }

      // Check if still referenced in links
      for (const l of Object.values(links)) {
        if (l.source === nodeId || l.target === nodeId) {
          return
        }
      }
      // Check if still referenced in case/if-else data
      for (const n of Object.values(nodes)) {
        if (n.type === NodeType.CASE) {
          if (n.data?.default_target === nodeId) {
            return
          }
          if (n.data?.cases?.some(c => c.target === nodeId)) {
            return
          }
        } else if (n.type === NodeType.IF_ELSE) {
          if (n.data?.else_target === nodeId) {
            return
          }
          if (n.data?.conditions?.some(c => c.target === nodeId)) {
            return
          }
        }
      }
      delete nodes[nodeId]
    }

    if (sourceNodeType === NodeType.CASE) {
      let prevTarget = null
      const cases = sourceData.cases

      // First try to find by the provided link id
      let edgeIndex = cases.findIndex(c => c.edge_uid === id)

      // If not found and we have an existingEdgeUid from stale reference, use that
      if (edgeIndex === -1 && existingEdgeUid) {
        edgeIndex = cases.findIndex(c => c.edge_uid === existingEdgeUid)
      }

      if (edgeIndex > -1) {
        prevTarget = cases[edgeIndex].target
        cases[edgeIndex].target = targetId
        // Update the edge_uid to the new link id
        cases[edgeIndex].edge_uid = id
      } else if (isDefaultLink || (link && link.target === sourceData.default_target)) {
        prevTarget = sourceData.default_target
        sourceData.default_target = targetId
      } else {
        // Create new case item with proper metadata
        const newCase = { edge_uid: id, target: targetId, condition: '??' }
        if (link?.metadata) {
          newCase.metadata = link.metadata
        }
        cases.push(newCase)
      }

      // Create or update the link
      if (link) {
        links[id].target = targetId
        // Ensure metadata is preserved for default links
        if (isDefaultLink && !link.metadata?.isDefault) {
          links[id].metadata = { ...link.metadata, isDefault: true }
        }
      } else {
        // Link doesn't exist, create it
        this.addNewLink({ edge_uid: id, source: sourceId, target: targetId, label: '??' }, workflowState)
      }

      // Delete old stale link if it exists and is different from the new one
      if (existingEdgeUid && existingEdgeUid !== id && links[existingEdgeUid]) {
        delete links[existingEdgeUid]
      }

      // Directly update the node data without calling updateNodeProperties to avoid re-processing
      nodes[sourceId].data = {
        ...sourceData,
        form: {
          ...sourceData.form,
          cases: cases,
          default_target: sourceData.default_target,
          attribute: sourceData.attribute,
          data: {
            ...sourceData.form?.data,
            cases: cases,
            default_target: sourceData.default_target,
            attribute: sourceData.attribute
          }
        }
      }
      cleanupTempNode(prevTarget)

      // Ensure orphan temporary nodes are cleaned up
      this.#cleanupTemporaryNodes(workflowState, [targetId])
    } else if (sourceNodeType === NodeType.IF_ELSE) {
      let prevTarget = null
      const conditions = sourceData.conditions

      // First try to find by the provided link id
      let condIndex = conditions.findIndex(c => c.edge_uid === id)

      // If not found and we have an existingEdgeUid from stale reference, use that
      if (condIndex === -1 && existingEdgeUid) {
        condIndex = conditions.findIndex(c => c.edge_uid === existingEdgeUid)
      }

      if (condIndex > -1) {
        // Found a matching condition — update its target and edge_uid
        prevTarget = conditions[condIndex].target
        conditions[condIndex].target = targetId
        conditions[condIndex].edge_uid = id
      } else if (isElseLink || (link && link.target === sourceData.else_target)) {
        // This is the else link — update else_target
        prevTarget = sourceData.else_target
        sourceData.else_target = targetId
      } else {
        // No matching condition and not the else link — add a new condition
        const newCondition = { edge_uid: id, target: targetId, condition: '??' }
        if (link?.metadata) {
          newCondition.metadata = link.metadata
        }
        conditions.push(newCondition)
      }

      // Create or update the link
      if (link) {
        links[id].target = targetId
        // Ensure metadata is preserved for else links
        if (isElseLink && !link.metadata?.isElse) {
          links[id].metadata = { ...link.metadata, isElse: true }
        }
      } else {
        // Link doesn't exist, create it
        const label = isElseLink ? 'else' : '??'
        const metadata = isElseLink ? { isElse: true } : {}
        this.addNewLink({ edge_uid: id, source: sourceId, target: targetId, label, metadata }, workflowState)
      }

      // Delete old stale link if it exists and is different from the new one
      if (existingEdgeUid && existingEdgeUid !== id && links[existingEdgeUid]) {
        delete links[existingEdgeUid]
      }

      // Directly update the node data without calling updateNodeProperties to avoid re-processing
      nodes[sourceId].data = {
        ...sourceData,
        form: {
          ...sourceData.form,
          conditions: sourceData.conditions,
          else_target: sourceData.else_target,
          data: {
            ...sourceData.form?.data,
            conditions: sourceData.conditions,
            else_target: sourceData.else_target
          }
        }
      }
      cleanupTempNode(prevTarget)

      // Ensure orphan temporary nodes are cleaned up
      this.#cleanupTemporaryNodes(workflowState, [targetId])
    } else {
      // Regular node: if the link exists, update its target; otherwise create a new link
      if (link) {
        const prevTarget = link.target
        link.target = targetId
        cleanupTempNode(prevTarget)
      } else {
        // Link doesn't exist yet (e.g. created via halo drag) — add it to the workflow
        const newLink = LinkFactory.createEdge({
          edge_uid: existingEdgeUid || id,
          source: sourceId,
          target: targetId,
          label: ''
        })
        this.#addLink(newLink, workflowState)
      }
    }

    return true
  }
  /**
   * Adds a node to the workflow state.
   * @param {WorkflowNode} node - The node to add.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {string} The ID of the added node.
   * @private
   */
  static #addNode(node, workflowState) {
    workflowState.nodes[node.node_uid] = node
    return node.node_uid
  }

  /**
   * Adds a link to the workflow state.
   * @param {WorkflowEdge} link - The link to add.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {string} The ID of the added link.
   * @private
   */
  static #addLink(link, workflowState) {
    workflowState.links[link.edge_uid] = link
    return link.edge_uid
  }

  /**
   * Gets the links connected to a node.
   * @param {string} nodeId - The ID of the node.
   * @param {{[key: string]: WorkflowEdge}} links - The links in the workflow state.
   * @returns {WorkflowEdge[]} The links connected to the node.
   * @private
   */
  static #getNodeConnectedLinks(nodeId, links) {
    return Object.values(links).filter(l => l.source === nodeId || l.target === nodeId)
  }

  /**
   * Restores a JointJS link to its previous target node, connecting to the inport magnet.
   * @param {*} linkView - The JointJS link view object.
   * @param {string} previousTargetId - The ID of the previous target node.
   * @private
   */
  static #restoreLinkToPreviousTarget(linkView, previousTargetId) {
    if (!previousTargetId || !linkView?.model) {
      return
    }

    // Restore the link target using the same format as NodeLink.create()
    // which uses { id: target, selector: 'inport' }
    linkView.model.target({
      id: previousTargetId,
      selector: 'inport'
    })
  }

  /**
   * Gets palette node template by type and subtype
   * @param {string} type - The node type
   * @param {string} subtype - The node subtype (optional)
   * @param {Array} paletteNodes - The palette nodes array
   * @returns {PaletteNode|null} The matching palette node template or null
   */
  static #getPaletteNodeTemplate(type, subtype, paletteNodes) {
    // Search through all palette sections
    for (const section of paletteNodes) {
      if (section.items) {
        const template = section.items.find(item => item.type === type && (!subtype || item.subtype === subtype))
        if (template) {
          return template
        }
      }
    }
    return null
  }

  /**
   * Creates a temporary target node for an if-else condition item.
   * @param {Object} conditionItem - The condition item to create a temporary target for.
   * @param {string} sourceNodeId - The ID of the source if-else node.
   * @param {DiagramPosition} position - The position to place the temporary node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {Object} The updated condition item with temporary target.
   * @private
   */
  static #createConditionItemTemporaryTarget(conditionItem, sourceNodeId, position, workflowState) {
    const tempNodeId = this.addNewNode(NodeType.TEMPORARY, position, workflowState, conditionItem.target)
    const linkId = this.addNewLink(
      {
        edge_uid: conditionItem.edge_uid || createUUID(),
        source: sourceNodeId,
        target: tempNodeId,
        label: conditionItem.condition || '??',
        metadata: conditionItem.metadata || {}
      },
      workflowState
    )

    return {
      ...conditionItem,
      condition: conditionItem.condition || '??',
      target: tempNodeId,
      edge_uid: linkId
    }
  }

  static #cleanupTemporaryNodes(workflowState, excludeNodeIds = []) {
    const { nodes, links } = workflowState
    if (!nodes || !links) {
      return
    }

    // Build sets in a single pass for O(1) lookups
    const connectedNodeIds = new Set()
    const referencedNodeIds = new Set()
    const excludeSet = excludeNodeIds.length > 0 ? new Set(excludeNodeIds) : null

    // Collect connected nodes from links
    for (const link of Object.values(links)) {
      if (link.source) {
        connectedNodeIds.add(link.source)
      }
      if (link.target) {
        connectedNodeIds.add(link.target)
      }
    }

    // Collect referenced nodes from case/if-else data
    for (const node of Object.values(nodes)) {
      const nodeType = node.type
      const data = node.data

      if (nodeType === NodeType.CASE) {
        if (data?.default_target) {
          referencedNodeIds.add(data.default_target)
        }
        if (data?.cases) {
          for (const caseItem of data.cases) {
            if (caseItem.target) {
              referencedNodeIds.add(caseItem.target)
            }
          }
        }
      } else if (nodeType === NodeType.IF_ELSE) {
        if (data?.else_target) {
          referencedNodeIds.add(data.else_target)
        }
        if (data?.conditions) {
          for (const conditionItem of data.conditions) {
            if (conditionItem.target) {
              referencedNodeIds.add(conditionItem.target)
            }
          }
        }
      }
    }

    // Remove orphan temporary nodes
    const removedNodeIds = []
    for (const nodeId in nodes) {
      const node = nodes[nodeId]
      if (
        node.type === NodeType.TEMPORARY &&
        !connectedNodeIds.has(nodeId) &&
        !referencedNodeIds.has(nodeId) &&
        (!excludeSet || !excludeSet.has(nodeId))
      ) {
        removedNodeIds.push(nodeId)
        delete nodes[nodeId]
      }
    }
  }

  /**
   * Post-processes a node after it has been added to the workflow state.
   * This method is specifically designed for "Case" nodes and performs the following:
   * - Updates each case in the node's data by creating a temporary target node and link.
   * - Creates a default target for the node by adding a temporary target node and link.
   *
   * @param {WorkflowNode} node - The node to be post-processed.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @private
   */
  static #postProcessNode(node, workflowState) {
    if (node.type === NodeType.CASE) {
      // Only process cases if the node doesn't already have a complete setup
      const hasExistingConnections = Object.values(workflowState.links).some(link => link.source === node.node_uid)

      if (!hasExistingConnections) {
        // Only create temporary targets for cases that don't already have valid targets
        node.data.cases = node.data.cases.map(caseItem => {
          // If the case already has a valid target and edge_uid, don't recreate it
          if (caseItem.target && caseItem.edge_uid && workflowState.nodes[caseItem.target]) {
            return caseItem
          }
          return this.#createCaseItemTemporaryTarget(caseItem, node.node_uid, node.ui.position, workflowState)
        })

        // Only create default target if it doesn't exist
        if (!node.data.default_target || !workflowState.nodes[node.data.default_target]) {
          const defaultTarget = this.#createCaseItemTemporaryTarget(
            { condition: 'default', edge_uid: '', target: '', metadata: { isDefault: true } },
            node.node_uid,
            node.ui.position,
            workflowState
          )
          node.data.default_target = defaultTarget.target
        }
      }

      this.#cleanupTemporaryNodes(workflowState, [])
    } else if (node.type === NodeType.IF_ELSE) {
      node.data.conditions = node.data.conditions.map(conditionItem => {
        return this.#createConditionItemTemporaryTarget(conditionItem, node.node_uid, node.ui.position, workflowState)
      })
      const elseTarget = this.#createConditionItemTemporaryTarget(
        { condition: 'else', edge_uid: '', target: '', metadata: { isElse: true } },
        node.node_uid,
        node.ui.position,
        workflowState
      )
      node.data.else_target = elseTarget.target
    }
  }

  /**
   * Updates the edges of an if-else node based on changes in its conditions list.
   * This method handles adding new edges, removing obsolete edges, and updating existing edges.
   * @param {string} id - The ID of the if-else node being updated.
   * @param {WorkflowNodeData} previousData - The previous data of the if-else node, including its conditions list.
   * @param {WorkflowNodeData} newData - The new data of the if-else node, including its updated conditions list.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @private
   */
  static #updateIfElseNodeEdges(id, previousData, newData, workflowState) {
    try {
      // Shallow copy conditions array - avoid cloneDeep for large arrays
      const conditionsList = newData.conditions ? [...newData.conditions] : null

      if (!conditionsList || !Array.isArray(conditionsList)) {
        return newData
      }

      const { links, nodes } = workflowState

      // Build lookup map for previous conditions - O(1) lookups instead of O(n)
      const previousConditionsMap = new Map()
      for (const c of previousData.conditions || []) {
        if (c.edge_uid) {
          previousConditionsMap.set(c.edge_uid, c)
        }
      }

      // Build lookup map for new conditions
      const newConditionsMap = new Map()
      for (const c of conditionsList) {
        if (c.edge_uid) {
          newConditionsMap.set(c.edge_uid, c)
        }
      }

      // Categorize conditions in single pass
      const added = []
      const updated = []
      const needsLinkRepair = []
      const previousTempTargets = []

      for (let i = 0; i < conditionsList.length; i++) {
        const condition = conditionsList[i]
        const edgeUid = condition.edge_uid

        if (!edgeUid) {
          // New condition without edge_uid - needs to be added
          added.push({ condition, index: i })
        } else if (!previousConditionsMap.has(edgeUid)) {
          // New condition with edge_uid not in previous - check if link exists
          if (links[edgeUid]) {
            // Link exists, just update
            updated.push({ condition, index: i })
          } else {
            // New condition - needs temp node and link created
            added.push({ condition, index: i })
          }
        } else {
          // Existing condition - check if link still exists
          if (!links[edgeUid]) {
            // Link was deleted - needs repair
            needsLinkRepair.push({ condition, index: i })
          } else {
            // Check if target or condition changed - if so, mark as updated
            const prevCondition = previousConditionsMap.get(edgeUid)
            if (prevCondition.target !== condition.target || prevCondition.condition !== condition.condition) {
              // Track previous temp target for cleanup
              if (
                prevCondition.target !== condition.target &&
                nodes[prevCondition.target]?.type === NodeType.TEMPORARY
              ) {
                previousTempTargets.push(prevCondition.target)
              }
              updated.push({ condition, index: i })
            }
            // If no changes, no action needed
          }
        }
      }

      // Find removed - check both by edge_uid and by finding orphaned links
      const removed = []
      for (const prevCond of previousData.conditions || []) {
        if (prevCond.edge_uid && !newConditionsMap.has(prevCond.edge_uid)) {
          removed.push(prevCond)
          if (nodes[prevCond.target]?.type === NodeType.TEMPORARY) {
            previousTempTargets.push(prevCond.target)
          }
        }
      }

      // Also find orphaned links - links from this if-else node that aren't in the new conditions list
      // This handles cases where edge_uid was generated locally but doesn't match workflow
      const newConditionEdgeUids = new Set(conditionsList.map(c => c.edge_uid).filter(Boolean))
      const elseTargetNodeId = newData.else_target

      for (const link of Object.values(links)) {
        if (link.source === id) {
          // Skip else link
          if (link.metadata?.isElse || link.label === 'else' || link.target === elseTargetNodeId) {
            continue
          }
          // If this link isn't in the new conditions, it's orphaned
          if (!newConditionEdgeUids.has(link.edge_uid)) {
            // Check if we already added this via the previous loop
            if (!removed.some(r => r.edge_uid === link.edge_uid)) {
              removed.push({ edge_uid: link.edge_uid, target: link.target })
              if (nodes[link.target]?.type === NodeType.TEMPORARY) {
                previousTempTargets.push(link.target)
              }
            }
          }
        }
      }

      const position = nodes[id].ui.position

      // Handle removed conditions FIRST - delete links and track temp nodes for cleanup
      for (const conditionItem of removed) {
        if (conditionItem.edge_uid && links[conditionItem.edge_uid]) {
          // Delete the link
          delete links[conditionItem.edge_uid]
        }
        // Delete temporary target node directly
        if (conditionItem.target && nodes[conditionItem.target]?.type === NodeType.TEMPORARY) {
          delete nodes[conditionItem.target]
        }
      }

      // Handle conditions that need link repair (edge_uid exists but link doesn't)
      for (const { condition, index } of needsLinkRepair) {
        // Check if target node exists
        if (!nodes[condition.target]) {
          // Create new temp node and link
          const updatedCondition = this.#createConditionItemTemporaryTarget(
            condition,
            id,
            { x: position.x + 360, y: position.y + index * 140 },
            workflowState
          )
          conditionsList[index] = updatedCondition
        } else {
          // Target exists but link doesn't - create new link
          const linkId = this.addNewLink(
            {
              source: id,
              target: condition.target,
              label: condition.condition || '??',
              metadata: condition.metadata || {}
            },
            workflowState
          )
          conditionsList[index] = { ...condition, edge_uid: linkId }
        }
      }

      // Handle added conditions - create temp nodes and links
      for (const { condition, index } of added) {
        // Check if target node exists
        if (condition.target && nodes[condition.target]) {
          // Target exists - just create the link
          const linkId = this.addNewLink(
            {
              source: id,
              target: condition.target,
              label: condition.condition || '??',
              metadata: condition.metadata || {}
            },
            workflowState
          )
          conditionsList[index] = { ...condition, edge_uid: linkId }
        } else {
          // Create temp node and link
          const updatedCondition = this.#createConditionItemTemporaryTarget(
            condition,
            id,
            { x: position.x + 360, y: position.y + index * 140 },
            workflowState
          )
          conditionsList[index] = updatedCondition
        }
      }

      // Handle updated conditions - update link labels and targets
      for (const { condition } of updated) {
        if (condition.edge_uid && links[condition.edge_uid]) {
          const link = links[condition.edge_uid]
          const previousTarget = link.target

          // Update label
          link.label = condition.condition || '??'

          // Update target if it changed
          if (condition.target && condition.target !== previousTarget) {
            // Verify target node exists
            if (nodes[condition.target]) {
              link.target = condition.target

              // Cleanup old temporary target if no longer referenced
              if (previousTarget && nodes[previousTarget]?.type === NodeType.TEMPORARY) {
                let isStillReferenced = false
                // Check if any other link references it
                for (const l of Object.values(links)) {
                  if (l.target === previousTarget || l.source === previousTarget) {
                    isStillReferenced = true
                    break
                  }
                }
                // Check if else_target references it
                if (!isStillReferenced && newData.else_target === previousTarget) {
                  isStillReferenced = true
                }
                // Check if any other condition references it
                if (!isStillReferenced) {
                  for (const c of conditionsList) {
                    if (c.target === previousTarget && c.edge_uid !== condition.edge_uid) {
                      isStillReferenced = true
                      break
                    }
                  }
                }
                if (!isStillReferenced) {
                  delete nodes[previousTarget]
                }
              }
            }
          }
        }
      }

      // Clean up old else target node if it changed and was temporary
      if (newData.else_target !== previousData.else_target) {
        if (previousData.else_target && nodes[previousData.else_target]?.type === NodeType.TEMPORARY) {
          let isStillReferenced = false
          for (const link of Object.values(links)) {
            if (link.target === previousData.else_target) {
              isStillReferenced = true
              break
            }
          }
          if (!isStillReferenced) {
            delete nodes[previousData.else_target]
          }
        }
      }

      // Ensure else target node exists
      if (!newData.else_target || !nodes[newData.else_target]) {
        const elseLink = Object.values(links).find(
          link => link.source === id && (link.metadata?.isElse || link.label === 'else')
        )
        const elseTarget = this.#createConditionItemTemporaryTarget(
          {
            condition: 'else',
            edge_uid: elseLink?.edge_uid || '',
            target: newData.else_target || elseLink?.target || '',
            metadata: { isElse: true }
          },
          id,
          { x: position.x + 360, y: position.y + conditionsList.length * 140 + 100 },
          workflowState
        )
        newData.else_target = elseTarget.target
      }

      // Deduplicate else links: ensure only one else link remains
      const elseLinks = Object.values(links).filter(
        link => link.source === id && (link.metadata?.isElse || link.label === 'else')
      )
      if (elseLinks.length > 1) {
        for (let i = 1; i < elseLinks.length; i++) {
          delete links[elseLinks[i].edge_uid]
        }
      }

      let elseLink = elseLinks[0] || null

      if (elseLink) {
        if (!elseLink.metadata) {
          elseLink.metadata = {}
        }
        elseLink.metadata.isElse = true
        if (elseLink.target !== newData.else_target) {
          elseLink.target = newData.else_target
        }
      } else {
        this.addNewLink(
          { source: id, target: newData.else_target, label: 'else', metadata: { isElse: true } },
          workflowState
        )
      }

      // Return updated data with synced conditions
      return {
        ...newData,
        conditions: conditionsList
      }
    } catch {
      return newData
    }
  }
  /**
   * Updates the edges of a case node based on changes in its cases list.
   * This method handles adding new edges, removing obsolete edges, and updating existing edges.
   * @param {string} id - The ID of the case node being updated.
   * @param {WorkflowNodeData} previousData - The previous data of the case node, including its cases list.
   * @param {WorkflowNodeData} newData - The new data of the case node, including its updated cases list.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {WorkflowNodeData} The updated node data with modified cases
   * @private
   */
  static #updateCaseNodeEdges(id, previousData, newData, workflowState) {
    // Shallow copy cases array - avoid cloneDeep for large arrays
    const casesList = newData.cases ? [...newData.cases] : null

    if (!casesList || !Array.isArray(casesList)) {
      return { ...previousData, ...newData }
    }

    const { links, nodes } = workflowState
    const position = nodes[id].ui.position

    // Clean up old default target node if it changed and was temporary
    if (newData.default_target !== previousData.default_target) {
      if (previousData.default_target && nodes[previousData.default_target]?.type === NodeType.TEMPORARY) {
        let isStillReferenced = false
        for (const link of Object.values(links)) {
          if (link.target === previousData.default_target) {
            isStillReferenced = true
            break
          }
        }
        if (!isStillReferenced) {
          delete nodes[previousData.default_target]
        }
      }
    }

    // Ensure default target node exists
    if (!newData.default_target || !nodes[newData.default_target]) {
      const defaultLink = Object.values(links).find(
        link => link.source === id && (link.metadata?.isDefault || link.label === 'default')
      )
      const defaultTarget = this.#createCaseItemTemporaryTarget(
        {
          condition: 'default',
          edge_uid: defaultLink?.edge_uid || '',
          target: newData.default_target || defaultLink?.target || '',
          metadata: { isDefault: true }
        },
        id,
        position,
        workflowState
      )
      newData.default_target = defaultTarget.target
    }

    // Deduplicate default links: ensure only one default link remains
    const defaultLinks = Object.values(links).filter(
      link => link.source === id && (link.metadata?.isDefault || link.label === 'default')
    )
    if (defaultLinks.length > 1) {
      for (let i = 1; i < defaultLinks.length; i++) {
        delete links[defaultLinks[i].edge_uid]
      }
    }

    let defaultLink = defaultLinks[0] || null

    if (defaultLink) {
      if (!defaultLink.metadata) {
        defaultLink.metadata = {}
      }
      defaultLink.metadata.isDefault = true
      if (defaultLink.target !== newData.default_target) {
        defaultLink.target = newData.default_target
      }
    } else {
      this.addNewLink(
        { source: id, target: newData.default_target, label: 'default', metadata: { isDefault: true } },
        workflowState
      )
    }

    // Build lookup map for previous cases - O(1) lookups instead of O(n)
    const previousCasesMap = new Map()
    for (const c of previousData.cases) {
      if (c.edge_uid) {
        previousCasesMap.set(c.edge_uid, c)
      }
    }

    // Build lookup map for new cases
    const newCasesMap = new Map()
    for (const c of casesList) {
      if (c.edge_uid) {
        newCasesMap.set(c.edge_uid, c)
      }
    }

    // Categorize cases in single pass
    const added = []
    const updated = []
    const needsLinkRepair = [] // Cases with edge_uid but no corresponding link
    const previousTempTargets = []

    for (let i = 0; i < casesList.length; i++) {
      const caseItem = casesList[i]

      // Check if case has edge_uid but link doesn't exist - needs repair
      if (caseItem.edge_uid && !links[caseItem.edge_uid]) {
        needsLinkRepair.push({ item: caseItem, index: i })
        continue
      }

      if (!caseItem.edge_uid || !previousCasesMap.has(caseItem.edge_uid)) {
        added.push({ item: caseItem, index: i })
      } else {
        const prev = previousCasesMap.get(caseItem.edge_uid)
        if (
          prev.target !== caseItem.target ||
          prev.condition !== caseItem.condition ||
          !isEqual(prev.conditionObj, caseItem.conditionObj)
        ) {
          updated.push(caseItem)
          // Track previous temp target for cleanup
          if (prev.target !== caseItem.target && nodes[prev.target]?.type === NodeType.TEMPORARY) {
            previousTempTargets.push(prev.target)
          }
        }
      }
    }

    // Find removed cases
    const removed = []
    for (const prevCase of previousData.cases) {
      if (prevCase.edge_uid && !newCasesMap.has(prevCase.edge_uid)) {
        removed.push(prevCase)
        if (nodes[prevCase.target]?.type === NodeType.TEMPORARY) {
          previousTempTargets.push(prevCase.target)
        }
      }
    }

    // Check default_target change for temp cleanup
    if (newData.default_target !== previousData.default_target) {
      if (nodes[previousData.default_target]?.type === NodeType.TEMPORARY) {
        previousTempTargets.push(previousData.default_target)
      }
    }

    // Handle cases that need link repair (edge_uid exists but link doesn't)
    for (const { item: caseItem, index: caseIndex } of needsLinkRepair) {
      if (caseItem.target && nodes[caseItem.target]) {
        // Target exists, create the missing link
        const newLinkId = this.addNewLink(
          {
            source: id,
            target: caseItem.target,
            label: caseItem?.condition?.trim()?.length ? caseItem.condition : '??'
          },
          workflowState
        )
        casesList[caseIndex] = { ...caseItem, edge_uid: newLinkId }
      } else {
        // Target doesn't exist, create temp node and link
        const updatedCaseItem = this.#createCaseItemTemporaryTarget(
          { ...caseItem, edge_uid: '', target: '' },
          id,
          position,
          workflowState
        )
        casesList[caseIndex] = updatedCaseItem
      }
    }

    // Handle added cases
    for (const { item: caseItem, index: caseIndex } of added) {
      if (!nodes[caseItem.target]) {
        const updatedCaseItem = this.#createCaseItemTemporaryTarget(caseItem, id, position, workflowState)
        casesList[caseIndex] = updatedCaseItem
      } else if (!caseItem.edge_uid) {
        const newLinkId = this.addNewLink(
          {
            source: id,
            target: caseItem.target,
            label: caseItem?.condition?.trim()?.length ? caseItem.condition : '??'
          },
          workflowState
        )
        casesList[caseIndex] = { ...caseItem, edge_uid: newLinkId }
      } else {
        this.addNewLink(
          {
            edge_uid: caseItem.edge_uid,
            source: id,
            target: caseItem.target,
            label: caseItem?.condition?.trim()?.length ? caseItem.condition : '??'
          },
          workflowState
        )
      }
    }

    // Handle removed cases
    for (const caseItem of removed) {
      if (caseItem.edge_uid && links[caseItem.edge_uid]) {
        // Directly delete the link without calling removeLink to avoid recursion
        // The case has already been removed from casesList, so just delete the link
        delete links[caseItem.edge_uid]
      }
    }

    // Handle updated cases
    for (const caseItem of updated) {
      if (caseItem.target && caseItem.edge_uid) {
        const oldLink = links[caseItem.edge_uid]

        // Only check connection limits if the target is actually changing
        if (oldLink && oldLink.target !== caseItem.target) {
          // check connection limit and possible node connections
          // Pass the edge_uid being replaced so it's excluded from the count
          const result = this.evaluateNodeConnectionLimits(
            nodes[id],
            nodes[caseItem.target],
            workflowState,
            caseItem.edge_uid
          )

          // If connection limits are exceeded, skip this update and keep old target
          if (!result?.incoming || !result?.outgoing) {
            const errorMsg = !result?.incoming
              ? `Node "${nodes[caseItem.target]?.data?.label || ''}" has reached its incoming connection limit.`
              : `Node "${nodes[id]?.data?.label || ''}" has reached its outgoing connection limit.`
            toast.error(errorMsg)
            // Skip updating this case - keep the old link target
            continue
          }

          oldLink.target = caseItem.target
          oldLink.label = caseItem.condition || ''
        } else if (oldLink && oldLink.label !== (caseItem.condition || '')) {
          // Only update label if target is the same
          oldLink.label = caseItem.condition || ''
        }
      }
    }

    // Force cleanup of previous temp targets that are no longer referenced
    for (const tempNodeId of previousTempTargets) {
      const tempNode = nodes[tempNodeId]
      if (!tempNode || tempNode.type !== NodeType.TEMPORARY) {
        continue
      }

      // Check if still referenced in any link
      let isStillReferenced = false
      for (const link of Object.values(links)) {
        if (link.target === tempNodeId) {
          isStillReferenced = true
          break
        }
      }

      // Also check if still referenced in any case/if-else node data
      // For the current node (id), use newData since it hasn't been written yet
      if (!isStillReferenced) {
        for (const n of Object.values(nodes)) {
          // Skip the current node being updated - use newData instead
          if (n.node_uid === id) {
            // Check against newData (the updated data)
            if (newData?.default_target === tempNodeId) {
              isStillReferenced = true
              break
            }
            if (casesList?.some(c => c.target === tempNodeId)) {
              isStillReferenced = true
              break
            }
            continue
          }

          if (n.type === NodeType.CASE) {
            if (n.data?.default_target === tempNodeId) {
              isStillReferenced = true
              break
            }
            if (n.data?.cases?.some(c => c.target === tempNodeId)) {
              isStillReferenced = true
              break
            }
          } else if (n.type === NodeType.IF_ELSE) {
            if (n.data?.else_target === tempNodeId) {
              isStillReferenced = true
              break
            }
            if (n.data?.conditions?.some(c => c.target === tempNodeId)) {
              isStillReferenced = true
              break
            }
          }
        }
      }

      // If not referenced anywhere, delete it
      if (!isStillReferenced) {
        delete nodes[tempNodeId]
      }
    }

    this.#cleanupTemporaryNodes(workflowState, [])

    return { ...newData, cases: casesList }
  }

  /**
   * Updates the cases of a case node when a link is removed.
   * This method handles two scenarios:
   * - If `removeCaseWhenRemoveLink` is true, the case associated with the link is removed.
   * - Otherwise, a temporary node is created, and the case is updated to connect to the temporary node.
   *
   * After updating the cases, the workflow auto-layout is triggered to adjust the diagram.
   *
   * @param {string} linkId - The ID of the link being removed.
   * @param {WorkflowNode} sourceNode - The source node associated with the link.
   * @param {boolean} removeCaseCondition - Indicates whether to remove the case or replace it with a temp node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @private
   */
  static #updateCaseNodeWhenRemoveLink(linkId, sourceNode, removeCaseCondition, workflowState) {
    const { node_uid, ui } = cloneDeep(sourceNode)
    const data = cloneDeep(sourceNode.data)
    const { target } = workflowState.links[linkId]

    let sharedTempNodeId = null
    if (!removeCaseCondition) {
      // Create a single temp node to be used for all affected cases
      sharedTempNodeId = this.addNewNode(NodeType.TEMPORARY, ui.position, workflowState)
    }

    // Handle default_target if needed
    if (data.default_target === target) {
      if (!removeCaseCondition) {
        this.addNewLink(
          {
            source: node_uid,
            target: sharedTempNodeId,
            label: 'default',
            metadata: { isDefault: true }
          },
          workflowState
        )
        data.default_target = sharedTempNodeId
      } else {
        data.default_target = ''
      }
    }

    if (removeCaseCondition) {
      // Remove the case associated with the link from the condition list
      data.cases = data.cases.filter(caseItem => caseItem.edge_uid !== linkId)
    } else {
      // Use the shared temp node and create a new link for each affected case
      data.cases = data.cases.map(caseItem => {
        if (caseItem.edge_uid === linkId) {
          const newLinkId = this.addNewLink(
            {
              source: node_uid,
              target: sharedTempNodeId,
              label: caseItem?.condition?.trim()?.length ? caseItem.condition : '??'
            },
            workflowState
          )
          return {
            ...caseItem,
            target: sharedTempNodeId,
            edge_uid: newLinkId
          }
        }
        return caseItem
      })
    }

    data.form = {
      ...data.form,
      cases: data.cases,
      default_target: data.default_target,
      attribute: data.attribute,
      data: {
        ...data.form?.data,
        cases: data.cases,
        default_target: data.default_target,
        attribute: data.attribute
      }
    }

    if (!isEqual(sourceNode.data, data)) {
      workflowState.nodes[node_uid].data = data
    }
  }

  /**
   * Updates the conditions of an if-else node when a link is removed.
   * This method handles the following scenarios:
   * - Clears the else_target of an if-else node if it points to the removed link's target.
   * - Removes conditions from the conditions list if `removeCondition` is true.
   * - Updates the conditions list by adding a temporary node target and link if `removeCondition` is false.
   *
   * After updating the conditions, the workflow auto-layout is triggered to adjust the diagram.
   *
   * @param {string} linkId - The ID of the link being removed.
   * @param {WorkflowNode} sourceNode - The source if-else node associated with the link.
   * @param {boolean} removeCondition - Indicates whether to remove the condition or update it with a temporary node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @private
   */
  static #updateIfElseNodeWhenRemoveLink(linkId, sourceNode, removeCondition, workflowState) {
    const { node_uid, ui } = cloneDeep(sourceNode)
    const data = cloneDeep(sourceNode.data)
    const link = workflowState.links[linkId]

    if (!link) {
      return
    }

    const { target } = link

    // Check if this is an else link by metadata, label, or matching else_target
    const isElseLink = link.metadata?.isElse === true || link.label === 'else' || data.else_target === target

    // Check if this link is for a condition (not else)
    const isConditionLink = data.conditions?.some(c => c.edge_uid === linkId)

    // Create temp node for else link (always required) or for conditions when not removing
    let tempNodeId = null

    // Handle else link removal - else is always required, so create new temp node and link unless removeCondition is true
    if (isElseLink) {
      if (removeCondition) {
        data.else_target = ''
      } else {
        tempNodeId = this.addNewNode(NodeType.TEMPORARY, ui.position, workflowState)
        this.addNewLink(
          {
            source: node_uid,
            target: tempNodeId,
            label: 'else',
            metadata: { isElse: true }
          },
          workflowState
        )
        data.else_target = tempNodeId
      }
    }

    // Handle condition link removal
    if (isConditionLink) {
      if (removeCondition) {
        // Remove the condition associated with the link from the conditions list
        data.conditions = data.conditions.filter(conditionItem => conditionItem.edge_uid !== linkId)
      } else {
        // Create temp node if not already created
        if (!tempNodeId) {
          tempNodeId = this.addNewNode(NodeType.TEMPORARY, ui.position, workflowState)
        }
        // Update the condition to point to the temp node
        data.conditions = data.conditions.map(conditionItem => {
          if (conditionItem.edge_uid === linkId) {
            const newLinkId = this.addNewLink(
              {
                source: node_uid,
                target: tempNodeId,
                label: conditionItem.condition || '??'
              },
              workflowState
            )
            return {
              ...conditionItem,
              target: tempNodeId,
              edge_uid: newLinkId
            }
          }
          return conditionItem
        })
      }
    }

    data.form = {
      ...data.form,
      conditions: data.conditions,
      else_target: data.else_target,
      data: {
        ...data.form?.data,
        conditions: data.conditions,
        else_target: data.else_target
      }
    }

    // Actually update the node data in workflow state
    if (!isEqual(sourceNode.data, data)) {
      workflowState.nodes[node_uid].data = data
    }
  }

  /**@typedef {{ edge_uid: string, condition: string, target: string}} CaseItem */
  /**
   * Creates a temporary target node and a link for a given case item.
   * This method is used to handle cases where a temporary placeholder node
   * is required to maintain the workflow structure.
   *
   * - A new node of type 'temporary' is created at a position offset from the source node.
   * - A new link is created connecting the source node to the temporary node.
   * - The case item is updated with the new link ID and target node ID.

   * @param {CaseItem} caseItem - The case item to update, containing condition and edge information.
   * @param {string} sourceId - The ID of the source node.
   * @param {DiagramPosition} fromPosition - The position of the source node.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @returns {CaseItem} The updated case item with the new link ID and target node ID.
   * @private
   */
  static #createCaseItemTemporaryTarget(caseItem, sourceId, fromPosition, workflowState) {
    // Use the provided target if it exists and is not already in the workflow
    let placeholderId = caseItem.target
    let newLinkId

    if (!placeholderId || !workflowState.nodes[placeholderId]) {
      // If no target or target doesn't exist, generate a new temporary node
      placeholderId = this.addNewNode(NodeType.TEMPORARY, fromPosition, workflowState, placeholderId)
    }

    // Create the link - use existing edge_uid if provided, otherwise generate new one
    const linkData = {
      source: sourceId,
      target: placeholderId,
      label: caseItem?.condition?.trim()?.length ? caseItem.condition : '??'
    }

    // Preserve metadata if it exists in the case item
    if (caseItem.metadata) {
      linkData.metadata = caseItem.metadata
    }

    if (caseItem.edge_uid) {
      linkData.edge_uid = caseItem.edge_uid
    }

    newLinkId = this.addNewLink(linkData, workflowState)

    return { ...caseItem, condition: caseItem.condition || '??', edge_uid: newLinkId, target: placeholderId }
  }

  /**
   * Updates case nodes when a node is removed from the workflow.
   * @param {string} removedNodeId - The ID of the node being removed.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @param {boolean} removeCaseCondition - Whether to remove the case associated with the removed node.
   * @private
   */
  static #updateCaseNodesWhenRemoveNode(removedNodeId, workflowState, removeCaseCondition) {
    let sharedTempNodeId = null

    if (workflowState.nodes) {
      Object.values(workflowState.nodes).forEach(node => {
        if (node.type === NodeType.CASE) {
          const data = cloneDeep(node.data)
          let nodeUpdated = false

          // Check if removedNodeId is in cases
          const hasAffectedCases = data.cases.some(caseItem => caseItem.target === removedNodeId)

          // Check if removedNodeId is the default_target
          const hasAffectedDefaultTarget = data.default_target === removedNodeId

          if (hasAffectedCases || hasAffectedDefaultTarget) {
            if (!removeCaseCondition && !sharedTempNodeId) {
              // Create one shared temp node for all affected case nodes
              sharedTempNodeId = this.addNewNode(NodeType.TEMPORARY, node.ui.position, workflowState)
            }

            // Update cases
            if (hasAffectedCases) {
              data.cases = data.cases
                .map(caseItem => {
                  if (caseItem.target === removedNodeId) {
                    if (!removeCaseCondition) {
                      const newLinkId = this.addNewLink(
                        {
                          source: node.node_uid,
                          target: sharedTempNodeId,
                          label: caseItem?.condition?.trim()?.length ? caseItem.condition : '??'
                        },
                        workflowState
                      )
                      return {
                        ...caseItem,
                        target: sharedTempNodeId,
                        edge_uid: newLinkId
                      }
                    } else {
                      return null // Will be filtered out below
                    }
                  }
                  return caseItem
                })
                .filter(Boolean) // Remove null entries

              nodeUpdated = true
            }

            // Update default_target
            if (hasAffectedDefaultTarget) {
              if (!removeCaseCondition) {
                this.addNewLink(
                  {
                    source: node.node_uid,
                    target: sharedTempNodeId,
                    label: 'default',
                    metadata: { isDefault: true }
                  },
                  workflowState
                )
                data.default_target = sharedTempNodeId
              } else {
                data.default_target = ''
              }
              nodeUpdated = true
            }

            if (nodeUpdated) {
              data.form = {
                ...data.form,
                cases: data.cases,
                default_target: data.default_target,
                attribute: data.attribute,
                data: {
                  ...data.form?.data,
                  cases: data.cases,
                  default_target: data.default_target,
                  attribute: data.attribute
                }
              }
              workflowState.nodes[node.node_uid].data = data
            }
          }
        }
      })
    }
  }

  /**
   * Updates if-else nodes when a node is removed from the workflow.
   * @param {string} removedNodeId - The ID of the node being removed.
   * @param {WorkflowState} workflowState - The current state of the workflow.
   * @param {boolean} removeCondition - Whether to remove the condition associated with the removed node.
   * @private
   */
  static #updateIfElseNodesWhenRemoveNode(removedNodeId, workflowState, removeCondition) {
    let sharedTempNodeId = null

    Object.values(workflowState.nodes).forEach(node => {
      if (node.type === NodeType.IF_ELSE) {
        const data = cloneDeep(node.data)
        let nodeUpdated = false

        // Check if removedNodeId is in conditions
        const hasAffectedConditions = (data.conditions ?? []).some(
          conditionItem => conditionItem.target === removedNodeId
        )

        // Check if removedNodeId is the else_target
        const hasAffectedElseTarget = data.else_target === removedNodeId

        if (hasAffectedConditions || hasAffectedElseTarget) {
          if (!removeCondition && !sharedTempNodeId) {
            // Create one shared temp node for all affected if-else nodes
            sharedTempNodeId = this.addNewNode(NodeType.TEMPORARY, node.ui.position, workflowState)
          }

          // Update conditions
          if (hasAffectedConditions) {
            data.conditions = data.conditions
              .map(conditionItem => {
                if (conditionItem.target === removedNodeId) {
                  if (!removeCondition) {
                    const newLinkId = this.addNewLink(
                      {
                        source: node.node_uid,
                        target: sharedTempNodeId,
                        label: conditionItem.condition
                      },
                      workflowState
                    )
                    return {
                      ...conditionItem,
                      target: sharedTempNodeId,
                      edge_uid: newLinkId
                    }
                  } else {
                    return null // Will be filtered out below
                  }
                }
                return conditionItem
              })
              .filter(Boolean) // Remove null entries

            nodeUpdated = true
          }

          // Update else_target
          if (hasAffectedElseTarget) {
            if (!removeCondition) {
              this.addNewLink(
                {
                  source: node.node_uid,
                  target: sharedTempNodeId,
                  label: 'else',
                  metadata: { isElse: true }
                },
                workflowState
              )
              data.else_target = sharedTempNodeId
            } else {
              data.else_target = ''
            }
            nodeUpdated = true
          }

          if (nodeUpdated) {
            data.form = {
              ...data.form,
              conditions: data.conditions,
              else_target: data.else_target,
              data: {
                ...data.form?.data,
                conditions: data.conditions,
                else_target: data.else_target
              }
            }
            workflowState.nodes[node.node_uid].data = data
          }
        }
      }
    })
  }

  /**
   * Creates a workflow node from a palette node
   * @param {string} nodeId - The unique identifier for the node
   * @param {string} type - The node type
   * @param {DiagramPosition} position - The position of the node
   * @param {PaletteNode} [paletteItem] - The palette item template
   * @returns {WorkflowNode} The created workflow node
   */
  static createNodeFromPalette(nodeId, type, position, paletteItem) {
    // Find palette template if not provided
    if (!paletteItem) {
      paletteItem = this.#getPaletteNodeTemplate(type, null, PaletteNodes)
    }

    const nodeData = {
      type,
      label: paletteItem?.name || type,
      description: paletteItem?.description || '',
      data: {},
      position,
      ui: {
        icon: paletteItem?.icon || paletteItem?.node_icon_link || '',
        position,
        available_connections: paletteItem?.available_connections || []
      },
      node_uid: nodeId,
      subtype: paletteItem?.subtype || '',
      item_type: paletteItem?.item_type || '',
      available_connections: paletteItem?.available_connections || [],
      connection_limits: paletteItem?.connection_limits || {},
      paletteItem
    }

    return NodeFactory.createNode(nodeData)
  }
}
